import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { cn } from "@/lib/utils";
import { type CreativeElementCategory } from "@repo/api/creative-element";

type FilterGroup = {
  label: string;
  options: { tag: string; label: string }[];
};

const HUMAN_FILTER_GROUPS: FilterGroup[] = [
  {
    label: "Appearance",
    options: [
      { tag: "APPEARANCE_EAST_ASIAN", label: "East Asian" },
      { tag: "APPEARANCE_SOUTH_ASIAN", label: "South Asian" },
      { tag: "APPEARANCE_BLACK", label: "Black" },
      { tag: "APPEARANCE_WHITE", label: "White" },
      { tag: "APPEARANCE_HISPANIC", label: "Hispanic" },
      { tag: "APPEARANCE_MIDDLE_EASTERN", label: "Middle Eastern" },
      { tag: "APPEARANCE_MEDITERRANEAN", label: "Mediterranean" },
      { tag: "APPEARANCE_PACIFIC", label: "Pacific" },
    ],
  },
  {
    label: "Gender",
    options: [
      { tag: "GENDER_MAN", label: "Man" },
      { tag: "GENDER_WOMAN", label: "Woman" },
    ],
  },
  {
    label: "Age",
    options: [
      { tag: "AGE_CHILD", label: "Child" },
      { tag: "AGE_TEEN", label: "Teen" },
      { tag: "AGE_YOUNG_ADULT", label: "Young Adult" },
      { tag: "AGE_MIDDLE_AGED", label: "Middle Aged" },
      { tag: "AGE_SENIOR", label: "Senior" },
    ],
  },
  {
    label: "Clothing",
    options: [
      { tag: "OUTFIT_COZY_HOME", label: "Cozy Home" },
      { tag: "OUTFIT_EVERYDAY_STYLE", label: "Everyday Style" },
      { tag: "OUTFIT_POLISHED_CASUAL", label: "Polished Casual" },
      { tag: "OUTFIT_DRESSED_UP", label: "Dressed Up" },
      { tag: "OUTFIT_SUNSHINE_READY", label: "Sunshine Ready" },
      { tag: "OUTFIT_HEAVY_WINTER", label: "Heavy Winter" },
      { tag: "OUTFIT_POLISHED_WINTER", label: "Polished Winter" },
    ],
  },
];

const ALL_VALUE = "ALL";

export default function CreativeElementsFilters({
  activeTab,
  selectedTags,
  onSelectedTagsChange,
}: {
  activeTab: CreativeElementCategory;
  selectedTags: string[];
  onSelectedTagsChange: (tags: string[]) => void;
}) {
  if (activeTab !== "HUMAN") return null;

  const hasActiveFilters = selectedTags.length > 0;

  const handleFilterChange = (group: FilterGroup, value: string) => {
    const groupTags = group.options.map((o) => o.tag);
    const withoutGroup = selectedTags.filter((t) => !groupTags.includes(t));
    if (value === ALL_VALUE) {
      onSelectedTagsChange(withoutGroup);
    } else {
      onSelectedTagsChange([...withoutGroup, value]);
    }
  };

  const handleClearAll = () => {
    onSelectedTagsChange([]);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <h2 className="mb-5 font-semibold text-text-primary">Filters</h2>
      <div className="flex flex-col gap-4">
        {HUMAN_FILTER_GROUPS.map((group) => {
          const selectedTag =
            selectedTags.find((t) => group.options.some((o) => o.tag === t)) ??
            ALL_VALUE;
          const selectedLabel =
            group.options.find((o) => o.tag === selectedTag)?.label ??
            group.label;

          return (
            <div key={group.label} className="relative">
              <Select
                value={selectedTag}
                onValueChange={(value) => handleFilterChange(group, value)}
              >
                <SelectTrigger
                  className={cn(
                    "w-full",
                    selectedTag === ALL_VALUE && "text-text-secondary",
                  )}
                >
                  <SelectValue>{selectedLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Any {group.label}</SelectItem>
                  {group.options.map((option) => (
                    <SelectItem key={option.tag} value={option.tag}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}

        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="mt-2 cursor-pointer text-sm font-medium text-text-secondary"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
